import React, {
    KeyboardEvent,
    ClipboardEvent,
    useState,
    useRef,
    useEffect, useCallback
} from 'react';
import { ILoginReferrer, login } from '../../dal/data-provider';
import { IServerValidation } from '../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../domain/validation/validation';
import { getSimpleCaptchaValidationError } from '../../../../domain/captcha/simple-captcha';
import { getIsChina, getIsAuthenticated } from '../../../../domain/hidden-data-provider';
import { getRecaptchaToken, renderRecaptcha2, resetRecaptcha2, getRecaptcha2Error } from '../../../../domain/captcha/recaptcha-provider';
import Recaptcha3Text from '../../../../domain/captcha/recaptcha3-terms';
import { updateUserPurchasedData } from '../../purchased-products/user-purchased-products';
import { IFormControl } from '../../../../ui/forms/interfaces';
import FormTextField from '../../../../ui/forms/fields/text-field';
import FormCheckboxField from '../../../../ui/forms/fields/checkbox-field';
import ChinaCaptchaField from '../../../../ui/forms/fields/captcha-china-field';
import FormTitle from '../../../../ui/forms/form-title';
import FormBorder from '../../../../ui/forms/form-border';
import FormPreloader from '../../../../ui/forms/form-preloader';
import { rvpSetNullToStorage } from '../../../recently-viewed-products/data/data-service';
import {
    getAppSuccessCallback,
    getWavesAppData, handleAppSuccess,
    WAVES_APP_DEFAULT_LOGIN_ERR
} from './waves-apps-provider';
import { IWavesAppData, IWavesAppDetection } from './interfaces';
import Preloader from '../../../../ui/svg/common/preloader';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';
import WavesAppsLoggedIn from './logged-in';
import ExternalLogin from '../external-login/external-login';
import { GOOGLE_ERROR, GOOGLE_SIGNED_IN } from '../../../../domain/google/google-login-provider';

interface IWavesAppsLogin {
    wavesAppDetectionData: IWavesAppDetection
}

/**
 * Waves Apps Login
 * https://wavesaudio.atlassian.net/browse/DSWEB-1917
 */
const WavesAppsLogin = (params: IWavesAppsLogin) => {

    const { wavesAppDetectionData } = params;
    const [loggedInScreenShown, setLoggedInScreenShown] = useState(getIsAuthenticated());
    const [isAfterLogin, setIsAfterLogin] = useState(false);

    const userNameRef = useRef<HTMLInputElement>(null); // used for focus
    const isChina = getIsChina();

    // validation ---------------------------------------
    const userNameControl = createControl('userName', '', {
        required: true
    });
    const passwordControl = createControl('password', '', {
        required: true
    });
    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [ isWavesAppLoading, setIsWavesAppLoading] = useState(true);
    const [ isLoading, setIsLoading] = useState(false);
    const [ wavesAppsError, setWavesAppsError] = useState('');
    const [ wavesAppData, setWavesAppData] = useState<IWavesAppData | null>(null);

    const [ response, setResponse] = useState<IServerValidation|null>();
    const [ userName, setUserName] = useState<IFormControl>(userNameControl);
    const [ password, setPassword] = useState<IFormControl>(passwordControl);
    const [ isPersistent, setIsPersistent] = useState(true);

    // simple captcha - used for chine
    const [ simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [ simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [ recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [ recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [ captchaError, setCaptchaError] = useState('');
    const { appCode, ts, uuid, qs, port } = wavesAppDetectionData;

    useEffect(() => {

        // load waves apps data
        (async () => {
            const response = await getWavesAppData(
                appCode || '',
                ts ?? 0,
            );

            setIsWavesAppLoading(false);

            if(!response ||
                response.statusCode !== 200 ||
                !response.data.isValid ||
                !response.data.relatedData ||
                !response.data.relatedData.relatedData){

                try{
                    // https://wavesaudio.atlassian.net/browse/CEN2-2206
                    const isInvalidToken = response?.data?.relatedData?.relatedData?.tokenInvalid;

                    if(isInvalidToken){
                        setWavesAppsError('Login failed, please ensure your computer clock is set to correct date and time (<a href="/support/how-to-solve-login-failed-error-with-date-and-time" title="">learn how</a>), then try to log in again.');
                        return;
                    }
                }
                catch(ex){
                    console.log(ex);
                }

                setWavesAppsError(WAVES_APP_DEFAULT_LOGIN_ERR);
                return;
            }

            const data = response.data.relatedData.relatedData as IWavesAppData;

            setWavesAppData(data);

            // https://wavesaudio.atlassian.net/browse/DSWEB-2064
            sendGAEvent('External Login', data.appName ?? '');
        })();

        // on page load -> focus on the username field
        if(!userNameRef || !userNameRef.current) return;
        userNameRef.current.focus();
    }, [appCode, ts]);

    const onLoginSuccess = useCallback(async () => {
        console.log('onLoginSuccess')
        // reset and reload user purchased products
        await updateUserPurchasedData();

        // clear recently viewed products storage
        rvpSetNullToStorage();

        const successCallback = wavesAppData?.successCallback ?? '';

        if(successCallback){
            // add additional query strings to success callback url
            console.log('wavesAppParams', wavesAppDetectionData);

            const url = getAppSuccessCallback(
                successCallback,
                qs ?? '',
                port ?? '',
            );
            console.log('url', url);

            // call the url or perform other actions depending on callback type
            try{
                await handleAppSuccess(wavesAppData?.callbackTypeID ?? '', url);
            }
            catch(ex){
                setWavesAppsError('An error has occurred.');
            }
        }

        // get app data again to show the 'logged in screen'
        const dataResponse = await getWavesAppData(
            appCode || '',
            ts ?? 0,
        );
        console.log('dataResponse', dataResponse)

        setIsLoading(false);

        const isError = !dataResponse ||
            dataResponse.statusCode !== 200 ||
            !dataResponse.data.isValid ||
            !dataResponse.data.relatedData ||
            !dataResponse.data.relatedData.relatedData;

        if(!isError){
            const data = dataResponse.data.relatedData.relatedData as IWavesAppData;

            // show 'logged in' screen with 2 buttons: 'Continue...' and 'Go to my account'
            setIsAfterLogin(true);
            setWavesAppData(data);
            setLoggedInScreenShown(true)
        }
    }, [
        appCode, port, qs, ts, wavesAppData?.successCallback, wavesAppData?.callbackTypeID, wavesAppDetectionData
    ]);

    const onLoginError = useCallback((response: {data: IServerValidation, statusCode: number } | null) => {
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    }, [
        isChina, recaptchaVersion, simpleCaptchaAttempts,
    ]);

    const googleError = useCallback((evt: CustomEvent) => {
        if(!evt || !evt.detail) return;
        onLoginError(evt.detail);
    }, [ onLoginError ]);

    const googleSignedIn = useCallback(async () => {
        await onLoginSuccess();
    }, [ onLoginSuccess ]);

    useEffect(() => {
        // @ts-ignore
        document.body.addEventListener(GOOGLE_ERROR, googleError);
        document.body.addEventListener(GOOGLE_SIGNED_IN, googleSignedIn);

        return () => {
            // @ts-ignore
            document.body.removeEventListener(GOOGLE_ERROR, googleError);
            document.body.removeEventListener(GOOGLE_SIGNED_IN, googleSignedIn);
        };
    }, [googleError, googleSignedIn]);

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * perform user login on server
     */
    const performLogin = async () => {

        // client side validation
        const isValid =
            isChina ?
            validateForm([
                [userName, setUserName],
                [password, setPassword],
                [simpleCaptchaCode, setSimpleCaptchaCode],
            ]) :
            validateForm([
                [userName, setUserName],
                [password, setPassword],
            ]);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }

            response = await login(
                ILoginReferrer.LoginPage,
                userName.value,
                password.value,
                isPersistent,
                captchaToken,
                recaptchaVersion,
                undefined,

                // waves apps like Central etc.
                // https://wavesaudio.atlassian.net/browse/DSWEB-1917
                appCode,
                ts,
                uuid,
                qs,
            );
        }
        catch(ex){}

        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            // all is well ---------------
            await onLoginSuccess();
            return;
        }

        // handle errors ---------
        setIsLoading(false);

        // render errors
        onLoginError(response);
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await performLogin();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    const externalLoginCallback = (response: { data: IServerValidation, statusCode: number } | null) => {

        (async () => {
            if( response &&
                response.statusCode === 200 &&
                response.data &&
                response.data.isValid) {
                await onLoginSuccess();
            }
            else{
                onLoginError(response);
            }
        })();

    };

    return (
        <>
            {
                isWavesAppLoading &&
                <FormBorder>
                    <div className="flex justify-center py-10 px-4">
                        <Preloader width="35px" height="35px" color="#4f4f4f" />
                    </div>
                </FormBorder>
            }

            {
                !isWavesAppLoading && wavesAppsError &&
                <FormBorder>
                    <div className="error-box" dangerouslySetInnerHTML={{__html: wavesAppsError }} role="alert"></div>
                </FormBorder>
            }

            {
                !isWavesAppLoading && !wavesAppsError &&
                <>
                    {
                        !loggedInScreenShown &&
                        <FormBorder>
                            <FormTitle title={ `Log in to Waves ${ wavesAppData?.appName || '' }` } />

                            <ExternalLogin
                                appCode={ appCode }
                                ts={ ts }
                                uuid={ uuid }
                                qs={ qs }
                                callback={ externalLoginCallback }
                                facebookText="Sign in with Facebook"
                                googleTextParam="signin_with"
                                dividerText="Or, sign in with your username"
                            />

                            <FormTextField
                                id={ 'username-tb' }
                                label={ 'Username' }
                                control={ userName }
                                maxLength={ 100 }
                                qa={ 'login-username' }
                                onChange={ evt => {
                                    const $target = evt.target as HTMLInputElement;
                                    setUserName(updateControl(userName, $target.value, !userName.isValid));
                                    setResponse(null); // empty server error
                                    setCaptchaError('');
                                }}
                                onBlur={ evt => {
                                    setUserName(validateControl(userName));
                                }}
                                onClick={ handleFieldClick }
                                onKeyUp={ handleEnter }
                                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                    setUserName(updateControl(userName, removeInvisibleCharsOnPaste(evt)));
                                }}
                            />

                            <FormTextField
                                id={ 'password-tb' }
                                label={ 'Password' }
                                control={ password }
                                maxLength={ 110 }
                                qa={ 'login-password' }
                                type={ 'password' }
                                onChange={ evt => {
                                    const $target = evt.target as HTMLInputElement;
                                    setPassword(updateControl(password, $target.value, !password.isValid));
                                    setResponse(null); // empty server error
                                    setCaptchaError('');
                                }}
                                onBlur={ evt => {
                                    setPassword(validateControl(password));
                                }}
                                onClick={ handleFieldClick }
                                onKeyUp={ handleEnter }
                                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                    setPassword(updateControl(password, removeInvisibleCharsOnPaste(evt)));
                                }}
                            />

                            <FormCheckboxField
                                id={ 'login-remember-me-cb' }
                                label={ 'Remember me' }
                                isChecked={ isPersistent }
                                onChange={ evt => {
                                    setIsPersistent(val => !val);
                                }}
                                onClick={ handleFieldClick }
                                additionalContent={
                                    <div className="flex items-center">
                                        <a
                                            className="text-sm no-underline"
                                            href="/forgot-password"
                                            data-qa="login-forgot-password"
                                            title="">Forgot Password?</a>
                                    </div>
                                }
                            />

                            {
                                /* ------------- google recaptcha version 2 or 3 ------------- */
                                !isChina &&
                                <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                            }

                            {
                                /* ------------- china simple captcha ------------- */
                                isChina &&
                                <ChinaCaptchaField
                                    control={ simpleCaptchaCode }
                                    setSimpleCaptchaCode={ setSimpleCaptchaCode }
                                    simpleCaptchaAttempts={ simpleCaptchaAttempts }
                                />
                            }

                            <div className="mb-2">

                                { isLoading && <FormPreloader /> }

                                {
                                    !isLoading &&
                                    <button
                                        className="w-btn"
                                        type="button"
                                        data-qa="login-button"
                                        onClick={async evt => {
                                            await performLogin();
                                        }}
                                    >Log in</button>
                                }
                            </div>

                            {
                                response && !response.isValid && (
                                    <div className="text-sm text-red my-4 text-center" role="alert">
                                        <ValidationErrors errors={ response.errors } />
                                    </div>
                                )
                            }

                            {
                                captchaError &&
                                <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID } role="alert">{ captchaError }</div>
                            }

                            <div className="text-center">
                                <a className="text-sm no-underline" href="/create-account" title="">Create New Account</a>
                            </div>

                            {
                                !isChina && recaptchaVersion === 3 &&
                                <div className="mt-8 text-center">
                                    <Recaptcha3Text />
                                </div>
                            }
                        </FormBorder>
                    }

                    { loggedInScreenShown &&
                        <WavesAppsLoggedIn
                            wavesAppDetectionData={ wavesAppDetectionData }
                            wavesAppData={ wavesAppData }
                            isAfterLogin={ isAfterLogin }
                            setLoggedInScreenShown={ setLoggedInScreenShown }
                        />
                    }
                </>
            }
        </>

    )
};

export default WavesAppsLogin;